import { Globe, Shield, Sparkles } from "lucide-react";
import {
  GlassCard,
  SolidBlock,
  HighlightBanner,
} from "@/app/components/brand-scaffold";
import {
  glassCardSamples,
  solidBlockSamples,
  bannerSamples,
  type GlassCardSample,
} from "@/design-system/registry/playground-containers";

const iconMap = {
  Sparkles,
  Shield,
  Globe,
} as const;

function renderGlassCard(s: GlassCardSample) {
  const Icon = iconMap[s.iconName];
  return (
    <GlassCard
      key={s.id}
      bgClass={s.bgClass}
      hoverClass={s.hoverClass}
      className={s.extraClassName}
    >
      <div className="mb-6 flex h-12 w-12 items-center justify-center border-4 border-black bg-black text-white">
        <Icon className="h-6 w-6" />
      </div>
      <h4 className="text-xl font-black uppercase mb-2">【{s.id}】{s.title}</h4>
      <p className={s.descriptionClassName}>{s.description}</p>
    </GlassCard>
  );
}

export function PlaygroundContainersSection() {
  return (
    <div>
      <div className="border-l-8 border-black pl-4 mb-10">
        <p className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-1">SECTION C</p>
        <h2 className="text-3xl font-black uppercase">容器级组件</h2>
      </div>

      <div className="flex flex-col gap-16">
        {/* C-1: GlassCard */}
        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【C.1】GLASSCARD — 毛玻璃卡片</h3>
          <p className="font-bold text-zinc-500 mb-6">透射底层网格背景，用于平铺展示并列特性。支持四种交互状态。</p>
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-6">
            {glassCardSamples.map(renderGlassCard)}
          </div>
        </div>

        {/* C-2: SolidBlock */}
        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【C.2】SOLIDBLOCK — 实体承压块</h3>
          <p className="font-bold text-zinc-500 mb-6">极度厚重稳定（8px 黑影），常用于大容器底板。纯色漆面，无透明度。背景色必须从品牌 9 色中选取。</p>
          <div className="space-y-6">
            {solidBlockSamples.map((s) => (
              <SolidBlock key={s.id} bgClass={s.bgClass}>
                <h4 className={s.titleClassName}>【{s.id}】{s.title}</h4>
                <p className={s.bgClass?.includes("text-white") ? "text-sm font-bold leading-relaxed text-white/90" : "text-sm font-bold text-zinc-400 leading-relaxed"}>
                  {s.description}
                </p>
              </SolidBlock>
            ))}
          </div>
        </div>

        {/* C-3: HighlightBanner */}
        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【C.3】HIGHLIGHTBANNER — 横幅总结块</h3>
          <p className="font-bold text-zinc-500 mb-6">单行通栏解释块，带有醒目的独立标记徽章。常吸附于主容器正下方。</p>
          <div className="flex flex-col gap-6">
            {bannerSamples.map((s) => (
              <HighlightBanner
                key={s.id}
                badgeText={`【${s.id}】${s.badgeText}`}
                badgeColor={s.badgeColor}
                shadowClass={s.shadowClass}
                className={s.interactive ? "cursor-pointer" : undefined}
                badgeIcon={s.hasIcon ? <Globe className="w-4 h-4 text-white" /> : undefined}
              >
                {s.description}
              </HighlightBanner>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
